import { JSX, memo } from "react";

import ChartWidget from "../widgets/ChartWidget";
import DataTableWidget from "../widgets/DataTableWidget";
import FunnelWidget from "../widgets/FunnelWidget";
import KpiWidget from "../widgets/KpiWidget";
import KpiWithDetailWidget from "../widgets/KpiWithDetailWidget";
import QuadrantScatterWidget from "../widgets/QuadrantScatterWidget";
import KpiQuadrantWidget from "../widgets/KpiQuadrantWidget";
import TieredBarWidget from "../widgets/TieredBarWidget";
import SparkleWidget from "../widgets/SparkleWidget";
import CustomBarWidget from "../widgets/CustomBarWidget";
import CustomTableWidget from "../widgets/CustomTableWidget";
import InfoBarWidget from "../widgets/InfoBarWidget";
import DonutPieWidget from "../widgets/DonutPieWidget";
import SentimentSplitWidget from "../widgets/SentimentSplitWidget";
import SingleStackBarWidget from "../widgets/SingleStackBarWidget";
import DenialReasonsWidget from "../widgets/DenialReasonsWidget";
import DeviationBarWidget from "../widgets/DeviationBarWidget";
import AverageBarWidget from "../widgets/AverageBarWidget";
import SubBarWidget from "../widgets/SubBarWidget";
import GroupedBarWidget from "../widgets/GroupedBarWidget";
import CardWidget from "../widgets/CardWidget";
import BarDrilldownWidget from "../widgets/BarDrilldownWidget";
import BarDrilldownCustomWidget from "../widgets/BarDrilldownCustomWidget";
import BubbleQuadrantWidget from "../widgets/BubbleQuadrantWidget";
import type { WidgetDownloadOptions } from "../widgets/DownloadOptionsMenu";
import {
  resolveWidgetConfig,
  type WidgetInstanceConfig,
} from "src/charts/widgetConfig";

type DashboardWidgetContentProps = {
  chartType: string;
  title: string;
  // Widget description from the dashboard detail API (kpi/sparkle include it
  // in their CSV export).
  description?: string | null;
  data: unknown;
  isLoading: boolean;
  hasError?: boolean;
  chartSpec?: Record<string, unknown>;
  isBuilderPreview?: boolean;
  dashboardName?: string;
  widgetTemplateId?: string | number;
  appliedFilters?: Record<string, unknown>;
  // Backend-declared download capability (siblings of the response's
  // `.data`) — surfaced by WidgetRenderer.tsx from the raw response since
  // widgetData itself gets stripped down to bare rows for most chart types.
  hasDownloadOptions?: boolean;
  downloadOptions?: WidgetDownloadOptions;
  // Self-fetching widgets only (custom_table/average_bar/deviation_bar/
  // cards): reports self-fetched has_download_options back up to
  // WidgetRenderer, which otherwise has no visibility into it (see e.g.
  // CustomTableWidget's own comment on this prop for why).
  onDownloadOptionsChange?: (hasDownloadOptions: boolean) => void;
};

// ---------------------------------------------------------------------------
// Special-case renderers — chart types that need props or behavior beyond the
// standard ChartWidget → ChartFactory → registry pipeline:
//
//   funnel          needs onPointClick for drilldown navigation
//   quadrant_table  needs onPointClick + renders a tier legend alongside the chart
//   data_table      not a Highcharts chart — uses DashboardTable component
//   kpi             single number display, no chart at all
//   kpi_with_detail kpi + expandable detail panel with its own sub-fetch
//   bar_drilldown   embeds its own Highcharts instance directly to keep
//                   metadata (headline/status/technical details) alongside
//                   the chart — the generic ChartWidget path drops metadata
//
// All other types (bar, pie, line, map, tiered_bar, force_bar, etc.) fall
// through to ChartWidget which delegates to ChartFactory and the registry.
// ---------------------------------------------------------------------------
type RendererFn = (
  p: DashboardWidgetContentProps & {
    displayTitle: string;
    // The author's per-widget display prefs, already validated. Resolved once
    // below and handed down as plain values rather than as the raw spec, so
    // the parse lives at one boundary instead of in every widget.
    widgetConfig: WidgetInstanceConfig;
  },
) => JSX.Element;

const SPECIAL_RENDERERS: Record<string, RendererFn> = {
  funnel: (p) => (
    <FunnelWidget
      isLoading={p.isLoading}
      title={p.displayTitle}
      description={p.description}
      data={p.data}
      widgetId={p.widgetTemplateId}
      dashboardName={p.dashboardName}
      appliedFilters={p.appliedFilters}
      hasDownloadOptions={p.hasDownloadOptions}
      downloadOptions={p.downloadOptions}
    />
  ),

  data_table: (p) => (
    <DataTableWidget
      isLoading={p.isLoading}
      data={p.data}
      title={p.title}
      dashboardName={p.dashboardName}
      widgetTemplateId={p.widgetTemplateId}
      appliedFilters={p.appliedFilters}
    />
  ),

  kpi: (p) => (
    <KpiWidget
      isLoading={p.isLoading}
      data={p.data}
      title={p.title}
      description={p.description}
      widgetId={p.widgetTemplateId}
      dashboardName={p.dashboardName}
      appliedFilters={p.appliedFilters}
      hasDownloadOptions={p.hasDownloadOptions}
      downloadOptions={p.downloadOptions}
    />
  ),

  kpi_with_detail: (p) => (
    <KpiWithDetailWidget
      title={p.title}
      isLoading={p.isLoading}
      data={p.data}
      dashboardName={p.dashboardName}
      widgetTemplateId={p.widgetTemplateId}
      appliedFilters={p.appliedFilters}
    />
  ),

  quadrant_table: (p) => (
    <QuadrantScatterWidget
      isLoading={p.isLoading}
      title={p.displayTitle}
      description={p.description}
      data={p.data}
      spec={p.chartSpec}
      widgetId={p.widgetTemplateId}
    />
  ),

  tiered_bar: (p) => (
    <TieredBarWidget
      title={p.displayTitle}
      data={p.data}
      widgetId={p.widgetTemplateId}
      chartSpec={p.chartSpec}
      dashboardName={p.dashboardName}
      widgetTemplateId={p.widgetTemplateId}
      appliedFilters={p.appliedFilters}
    />
  ),

  sparkle: (p) => (
    <SparkleWidget
      isLoading={p.isLoading}
      data={p.data}
      title={p.title}
      description={p.description}
      defaultTab={p.widgetConfig.default_tab}
      defaultHideChart={p.widgetConfig.hide_line_chart}
      widgetId={p.widgetTemplateId}
      dashboardName={p.dashboardName}
      appliedFilters={p.appliedFilters}
      hasDownloadOptions={p.hasDownloadOptions}
      downloadOptions={p.downloadOptions}
    />
  ),

  custom_bar: (p) => (
    <CustomBarWidget
      isLoading={p.isLoading}
      data={p.data}
      title={p.title}
      dashboardName={p.dashboardName}
      widgetTemplateId={p.widgetTemplateId}
      appliedFilters={p.appliedFilters}
    />
  ),

  custom_table: (p) => (
    <CustomTableWidget
      isLoading={p.isLoading}
      data={p.data}
      title={p.title}
      dashboardName={p.dashboardName}
      widgetTemplateId={p.widgetTemplateId}
      appliedFilters={p.appliedFilters}
      onDownloadOptionsChange={p.onDownloadOptionsChange}
    />
  ),

  info_bar: (p) => (
    <InfoBarWidget
      isLoading={p.isLoading}
      data={p.data}
      title={p.title}
      dashboardName={p.dashboardName}
      widgetTemplateId={p.widgetTemplateId}
      appliedFilters={p.appliedFilters}
    />
  ),

  donut_pie: (p) => (
    <DonutPieWidget
      isLoading={p.isLoading}
      data={p.data}
      title={p.title}
      dashboardName={p.dashboardName}
      widgetTemplateId={p.widgetTemplateId}
      appliedFilters={p.appliedFilters}
    />
  ),

  // Two 100% stacked bars (Agent / Customer). Deliberately NOT donut_pie — its
  // rows are two full distributions of the same calls, so one ring would double
  // count every call and render ~200%. See the component header.
  sentiment_split: (p) => (
    <SentimentSplitWidget
      isLoading={p.isLoading}
      data={p.data}
      title={p.title}
      dashboardName={p.dashboardName}
      widgetTemplateId={p.widgetTemplateId}
      appliedFilters={p.appliedFilters}
    />
  ),

  single_stack_bar: (p) => (
    <SingleStackBarWidget
      isLoading={p.isLoading}
      data={p.data}
      title={p.title}
      dashboardName={p.dashboardName}
      widgetTemplateId={p.widgetTemplateId}
      appliedFilters={p.appliedFilters}
    />
  ),

  custom_lines: (p) => (
    <DenialReasonsWidget
      isLoading={p.isLoading}
      data={p.data}
      title={p.title}
      dashboardName={p.dashboardName}
      widgetTemplateId={p.widgetTemplateId}
      appliedFilters={p.appliedFilters}
      onDownloadOptionsChange={p.onDownloadOptionsChange}
    />
  ),

  deviation_bar: (p) => (
    <DeviationBarWidget
      isLoading={p.isLoading}
      data={p.data}
      title={p.title}
      dashboardName={p.dashboardName}
      widgetTemplateId={p.widgetTemplateId}
      appliedFilters={p.appliedFilters}
      onDownloadOptionsChange={p.onDownloadOptionsChange}
    />
  ),

  average_bar: (p) => (
    <AverageBarWidget
      isLoading={p.isLoading}
      data={p.data}
      title={p.title}
      dashboardName={p.dashboardName}
      widgetTemplateId={p.widgetTemplateId}
      appliedFilters={p.appliedFilters}
      onDownloadOptionsChange={p.onDownloadOptionsChange}
    />
  ),

  // grouped_bar goes through a wrapper (not straight to ChartWidget) because
  // its payload can arrive split into segments — the wrapper owns the tabs
  // and hands the active one down as if it were the whole payload.
  grouped_bar: (p) => (
    <GroupedBarWidget
      isLoading={p.isLoading}
      title={p.displayTitle}
      description={p.description}
      data={p.data}
      spec={p.chartSpec}
      defaultTab={p.widgetConfig.default_tab}
      widgetId={p.widgetTemplateId}
      dashboardName={p.dashboardName}
      appliedFilters={p.appliedFilters}
      hasDownloadOptions={p.hasDownloadOptions}
      downloadOptions={p.downloadOptions}
    />
  ),

  sub_bar: (p) => (
    <SubBarWidget
      isLoading={p.isLoading}
      data={p.data}
      title={p.title}
      dashboardName={p.dashboardName}
      widgetTemplateId={p.widgetTemplateId}
      appliedFilters={p.appliedFilters}
    />
  ),

  cards: (p) => (
    <CardWidget
      isLoading={p.isLoading}
      data={p.data}
      title={p.title}
      dashboardName={p.dashboardName}
      widgetTemplateId={p.widgetTemplateId}
      appliedFilters={p.appliedFilters}
      onDownloadOptionsChange={p.onDownloadOptionsChange}
    />
  ),

  special_quadrant_chart: (p) => (
    <KpiQuadrantWidget
      title={p.displayTitle}
      data={p.data}
      widgetId={p.widgetTemplateId}
      chartSpec={p.chartSpec}
      dashboardName={p.dashboardName}
      widgetTemplateId={p.widgetTemplateId}
      appliedFilters={p.appliedFilters}
    />
  ),

  bar_drilldown: (p) => (
    <BarDrilldownWidget isLoading={p.isLoading} data={p.data} />
  ),

  bar_drilldown_custom: (p) => (
    <BarDrilldownCustomWidget isLoading={p.isLoading} data={p.data} />
  ),

  bubble_quadrant_chart: (p) => (
    <BubbleQuadrantWidget
      isLoading={p.isLoading}
      data={p.data}
      title={p.title}
      dashboardName={p.dashboardName}
      widgetTemplateId={p.widgetTemplateId}
      appliedFilters={p.appliedFilters}
      onDownloadOptionsChange={p.onDownloadOptionsChange}
    />
  ),
};

function DashboardWidgetContent(props: DashboardWidgetContentProps): JSX.Element {
  const { chartType, title, isBuilderPreview = false, hasError = false, chartSpec = {} } = props;

  if (hasError) return <p className="text-danger mb-0">Unable to fetch data</p>;

  // In builder preview the Slot header already shows the title — pass empty string inside the chart.
  const displayTitle = isBuilderPreview ? "" : title;

  // chartSpec carries the widget's query spec with the author's display
  // prefs merged in by the backend, so this picks out only the keys this
  // chart type declares and ignores the rest.
  const widgetConfig = resolveWidgetConfig(chartType, chartSpec);

  const renderSpecial = SPECIAL_RENDERERS[chartType];
  if (renderSpecial) {
    return renderSpecial({ ...props, chartSpec, displayTitle, widgetConfig });
  }

  return (
    <ChartWidget
      isLoading={props.isLoading}
      title={displayTitle}
      description={props.description}
      data={props.data}
      spec={chartSpec}
      chartType={chartType}
      widgetId={props.widgetTemplateId}
      dashboardName={props.dashboardName}
      appliedFilters={props.appliedFilters}
      hasDownloadOptions={props.hasDownloadOptions}
      downloadOptions={props.downloadOptions}
    />
  );
}

export default memo(DashboardWidgetContent);
